import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
} from "@xyflow/react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import "@xyflow/react/dist/style.css";
import { Tabs } from "../../../components/shared/tabs/tabs";
import { useUser } from "../../../functions/userContext";
import { ReportBuilderViewSideBar } from "./reportBuilder.sideBar";
import { modalComponents, nodeTypes } from "./nodes/nodeTypes";
import { ReportBuilderControls } from "./reportBuilder.controls";
import { ReportBuilderMiniMap } from "./reportBuilder.minimap";
import "./reportBuilder.css";
import { CustomEdge } from "./reportBuilder.customEdge";
import { ModalTypes } from "./modals/modalTypes";
import WorkflowView from "./workflowView";
import ExecutionsView from "./executionsView";
import WorkflowsOverview from "./workflowsOverview";
import { useTheme } from "@mui/material/styles";
import { callAPI } from "../../../functions/api";
import { SettingsModal } from "./modals/settingsModal";
import {
  startWorkflowSignalR,
  stopWorkflowSignalR,
  getWorkflowConnection,
} from "../../../functions/signalrService";
import { getCurrentUser } from "../../../functions/api";
import { useWorkflowManager } from "./reportBuilder.workflow";

export const ReportBuilder = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const { setShowTabs, setCurrentTab } = useUser();
  const sidebarRef = useRef();
  const controlsRef = useRef();

  // Initialize workflow manager
  const {
    currentWorkflow: workflow,
    createWorkflow,
    updateWorkflow,
    updateWorkflowName,
    updateWorkflowStatus,
    getWorkflowPayload,
    clearWorkflow,
  } = useWorkflowManager();

  const [idCounter, setIdCounter] = useState(1);
  const [nodes, setNodes, onNodesChangeDefault] = useNodesState([]);
  const [isDragging, setIsDragging] = useState(false);
  const lastPositionRef = useRef({});
  // Custom node change handler that distinguishes between drag and other operations
  const onNodesChange = useCallback(
    (changes) => {
      // Check if this is a drag operation (position change)
      const isDragOperation = changes.some(
        (change) => change.type === "position" && change.dragging === true
      );

      if (isDragOperation && !isDragging) {
        setIsDragging(true);
      } else if (!isDragOperation && isDragging) {
        // Wait a bit to ensure drag has completely finished
        setTimeout(() => {
          setIsDragging(false);
        }, 100);
      }

      onNodesChangeDefault(changes);
    },
    [isDragging, onNodesChangeDefault]
  );

  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [mode, setMode] = useState("trigger");
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsNode, setSettingsNode] = useState(null);
  const [customSettingsModalOpen, setCustomSettingsModalOpen] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Overview, 1 = Editor, 2 = Visualizer, 3 = Executions
  const tabTitles = ["Overview", "Editor", "Visualizer", "Executions"];
  const currentExecutionOrderRef = useRef([]);
  const currentTimeoutRef = useRef(null); // stores timeout ID for workflow execution
  const lastConnectedNode = useRef(null); // tracks the node that triggered sidebar
  const edgeTypes = {
    custom: CustomEdge,
    // ... any other edge types
  };

  useEffect(() => {
    if (tabTitles[activeTab]) {
      setCurrentTab(tabTitles[activeTab]);
    }
    return () => setCurrentTab(null);
  }, [activeTab, setCurrentTab]);

  useEffect(() => {
    setShowTabs(true);
  }, [setShowTabs]);

  // Cleanup SignalR when component unmounts
  useEffect(() => {
    return () => {
      stopWorkflowSignalR();
    };
  }, []); // Empty deps array means this only runs on mount/unmount

  // Update workflow whenever nodes or edges change
  // Only update workflow when nodes/edges are actually modified, not on every render
  const prevNodesRef = useRef();
  const prevEdgesRef = useRef();

  useEffect(() => {
    // Skip if no workflow exists
    if (!workflow) {
      prevNodesRef.current = nodes;
      prevEdgesRef.current = edges;
      return;
    }

    // Simple length check first
    const nodesLengthChanged =
      !prevNodesRef.current || prevNodesRef.current.length !== nodes.length;
    const edgesLengthChanged =
      !prevEdgesRef.current || prevEdgesRef.current.length !== edges.length;

    // Deep check only if lengths are the same but we suspect changes
    let nodesContentChanged = false;
    let edgesContentChanged = false;

    if (!nodesLengthChanged && prevNodesRef.current) {
      // Check for meaningful node changes (position, data, status changes don't count for workflow updates)
      nodesContentChanged = nodes.some((node, index) => {
        const prevNode = prevNodesRef.current[index];
        return (
          !prevNode ||
          prevNode.id !== node.id ||
          prevNode.type !== node.type ||
          JSON.stringify(prevNode.data?.input) !== JSON.stringify(node.data?.input) ||
          JSON.stringify(prevNode.data?.settings) !== JSON.stringify(node.data?.settings)
        );
      });
    }

    if (!edgesLengthChanged && prevEdgesRef.current) {
      // Check for edge changes
      edgesContentChanged = edges.some((edge, index) => {
        const prevEdge = prevEdgesRef.current[index];
        return (
          !prevEdge ||
          prevEdge.id !== edge.id ||
          prevEdge.source !== edge.source ||
          prevEdge.target !== edge.target
        );
      });
    }

    const shouldUpdate =
      nodesLengthChanged || edgesLengthChanged || nodesContentChanged || edgesContentChanged;

    if (shouldUpdate) {
      updateWorkflow(nodes, edges);
    }

    // Update refs
    prevNodesRef.current = nodes;
    prevEdgesRef.current = edges;
  }, [nodes, edges, workflow?.id, updateWorkflow]); // Use workflow.id instead of workflow object

  // Ensure workflow is created when first node is added
  useEffect(() => {
    if (nodes.length > 0 && !workflow) {
      try {
        const newWorkflow = createWorkflow(nodes, edges);
        if (!newWorkflow) {
        }
      } catch (error) {}
    }
  }, [nodes.length, workflow?.id, createWorkflow]); // Simplified dependencies

  // Set up SignalR connection for workflow updates
  useEffect(() => {
    if (!workflow?.id) {
      return;
    }

    const eventHandlers = {
      // Match backend SignalR event names
      WorkflowStarted: (data) => {
        const dataWorkflowId = data.id || data.Id; // Support both lowercase and uppercase
        if (dataWorkflowId === workflow.id) {
          updateWorkflowStatus("executing");
        }
      },
      NodeExecutionStarted: (data) => {
        const dataWorkflowId = data.id || data.Id; // Support both lowercase and uppercase

        if (dataWorkflowId === workflow.id) {
          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === data.nodeId) {
                return {
                  ...n,
                  status: "running",
                  statusMessage: `Executing... (Step ${data.step}/${data.totalSteps})`,
                };
              }
              return n;
            })
          );
        }
      },
      NodeExecutionCompleted: (data) => {
        const dataWorkflowId = data.id || data.Id; // Support both lowercase and uppercase

        if (dataWorkflowId === workflow.id) {
          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === data.nodeId) {
                // Check if the backend reported an error
                const hasError = data.status === "failed" || data.status === "error" || data.error;
                const isSuccessful =
                  !hasError &&
                  (data.status === "completed" || data.status === "success" || !data.status);

                // Special handling for filter nodes that may not have sampleData
                const isFilterNode =
                  n.type?.toLowerCase().includes("filter") ||
                  n.data?.label?.toLowerCase().includes("filter");

                // Special handling for Excel export nodes that return file download info
                const isExcelExportNode =
                  n.type === "export.excel" || n.data?.type === "export.excel";
                const hasFileResult = data.downloadUrl && data.filename && isSuccessful;

                const statusMessage = hasError
                  ? data.error || data.message || "Execution failed"
                  : data.message || "Completed successfully";

                // Debug: Store the event data for debugging
                window.lastNodeExecutionCompleted = {
                  timestamp: new Date().toISOString(),
                  nodeId: data.nodeId,
                  nodeName: data.nodeName,
                  status: data.status,
                  message: data.message,
                  sampleData: data.sampleData,
                  nodeExecutionOutput: data.nodeExecutionOutput,
                  hasError,
                  isSuccessful,
                  statusMessage,
                };

                return {
                  ...n,
                  status: hasError ? "error" : "completed",
                  statusMessage: statusMessage,
                  data: {
                    ...n.data,
                    executionState: {
                      lastExecuted: new Date().toISOString(),
                      sampleData: isSuccessful ? data.sampleData : undefined,
                      nodeExecutionOutput: isSuccessful ? data.nodeExecutionOutput : undefined,
                      recordCount: isSuccessful ? data.recordCount : undefined,
                      step: data.step,
                      success: isSuccessful,
                      error: hasError ? data.error || data.message : undefined,
                      isFilterNode: isFilterNode,
                      downloadUrl: isSuccessful ? data.downloadUrl : undefined,
                      filename: isSuccessful ? data.filename : undefined,
                      fileResult: hasFileResult
                        ? {
                            downloadUrl: data.downloadUrl,
                            filename: data.filename,
                            message: data.message,
                            generatedAt: new Date().toISOString(),
                          }
                        : undefined,
                    },
                  },
                };
              }
              return n;
            })
          );
        }
      },
      NodeExecutionFailed: (data) => {
        const dataWorkflowId = data.id || data.Id; // Support both lowercase and uppercase
        if (dataWorkflowId === workflow.id) {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === data.nodeId
                ? {
                    ...n,
                    status: "error",
                    statusMessage: data.error || "Execution failed",
                    data: {
                      ...n.data,
                      executionState: {
                        lastExecuted: new Date().toISOString(),
                        error: data.error,
                        step: data.step,
                        success: false,
                      },
                    },
                  }
                : n
            )
          );
        }
      },
      WorkflowCompleted: (data) => {
        const dataWorkflowId = data.id || data.Id; // Support both lowercase and uppercase
        if (dataWorkflowId === workflow.id) {
          // Clear timeout since workflow completed
          if (currentTimeoutRef.current) {
            clearTimeout(currentTimeoutRef.current);
            currentTimeoutRef.current = null;
          }

          // Debug: Store the event data for debugging
          window.lastWorkflowCompleted = {
            timestamp: new Date().toISOString(),
            workflowId: dataWorkflowId,
            status: data.status,
            nodeStatuses: data.nodeStatuses,
            currentExecutionOrder: currentExecutionOrderRef.current,
          };

          // Update final node statuses with complete execution data
          if (data.nodeStatuses && Array.isArray(data.nodeStatuses)) {
            setNodes((nds) =>
              nds.map((n) => {
                const nodeStatus = data.nodeStatuses.find((ns) => ns.nodeId === n.id);
                if (nodeStatus) {
                  // Check if the backend reported an error for this node
                  const hasError =
                    nodeStatus.status === "failed" ||
                    nodeStatus.status === "error" ||
                    nodeStatus.error;
                  const isSuccessful =
                    !hasError &&
                    (nodeStatus.status === "completed" || nodeStatus.status === "success");

                  // Check for file results in the node status (only if successful)
                  const hasFileResult =
                    nodeStatus.downloadUrl && nodeStatus.filename && isSuccessful;

                  const statusMessage = hasError
                    ? nodeStatus.error || nodeStatus.message || "Failed"
                    : nodeStatus.message || "Completed successfully";

                  return {
                    ...n,
                    status: hasError ? "error" : "completed",
                    statusMessage: statusMessage,
                    data: {
                      ...n.data,
                      executionState: {
                        ...(n.data.executionState || {}),
                        finalStatus: nodeStatus.status,
                        executionTime: nodeStatus.executionTime,
                        recordCount: isSuccessful ? nodeStatus.recordCount : undefined,
                        sampleData: isSuccessful ? nodeStatus.sampleData : undefined,
                        nodeExecutionOutput: isSuccessful
                          ? nodeStatus.nodeExecutionOutput
                          : undefined,
                        success: isSuccessful,
                        error: hasError ? nodeStatus.error || nodeStatus.message : undefined,
                        // Add file result information (only if successful)
                        downloadUrl: isSuccessful ? nodeStatus.downloadUrl : undefined,
                        filename: isSuccessful ? nodeStatus.filename : undefined,
                        fileResult: hasFileResult
                          ? {
                              downloadUrl: nodeStatus.downloadUrl,
                              filename: nodeStatus.filename,
                              message: nodeStatus.message || "File generated successfully",
                              generatedAt: new Date().toISOString(),
                            }
                          : undefined,
                      },
                    },
                  };
                } else {
                  // If node was part of execution but not in nodeStatuses,
                  // ensure it has a completion status if it was running/pending OR was in execution order
                  if (
                    n.status === "running" ||
                    n.status === "pending" ||
                    currentExecutionOrderRef.current.includes(n.id)
                  ) {
                    return {
                      ...n,
                      status: "completed",
                      statusMessage: "Completed successfully",
                    };
                  }
                }
                return n;
              })
            );
          } else {
            // No nodeStatuses provided by backend - update all nodes in execution order to completed
            setNodes((nds) =>
              nds.map((n) => {
                // If node was in execution order, mark it as completed
                if (currentExecutionOrderRef.current.includes(n.id)) {
                  return {
                    ...n,
                    status: "completed",
                    statusMessage: "Completed successfully",
                    data: {
                      ...n.data,
                      executionState: {
                        ...(n.data.executionState || {}),
                        lastExecuted: new Date().toISOString(),
                        success: true,
                      },
                    },
                  };
                }
                return n;
              })
            );
          }
          updateWorkflowStatus(data.status === "completed" ? "completed" : "failed");
        }
      },
      WorkflowFailed: (data) => {
        console.log("🔥🔥🔥 WorkflowFailed handler called!");
        console.log("🔥 WorkflowFailed received:", data);
        console.log(
          "🔥 WorkflowFailed data.nodeStatuses:",
          JSON.stringify(data.nodeStatuses, null, 2)
        );
        const dataWorkflowId = data.workflowId || data.id || data.Id; // Support all variations
        console.log("🔥 Workflow ID check:", {
          dataWorkflowId,
          currentWorkflowId: workflow.id,
          workflowObject: workflow,
          matches: dataWorkflowId === workflow.id,
        });

        if (dataWorkflowId === workflow.id) {
          console.log("🔥 WorkflowFailed - Processing for workflow:", dataWorkflowId);

          // Clear timeout since workflow failed
          if (currentTimeoutRef.current) {
            clearTimeout(currentTimeoutRef.current);
            currentTimeoutRef.current = null;
          }

          // Handle case where nodeStatuses is null - this is often a preliminary failure event
          if (data.nodeStatuses === null) {
            console.log(
              "🔥 WorkflowFailed with null nodeStatuses - marking all running/pending nodes as error"
            );
            setNodes((currentNodes) => {
              const updatedNodes = currentNodes.map((n) => {
                if (n.status === "running" || n.status === "pending" || n.status === "queued") {
                  console.log(
                    `🔥 Marking node ${n.id} as error (was ${n.status}) due to null nodeStatuses`
                  );
                  return {
                    ...n,
                    status: "error",
                    statusMessage: data.error || "Workflow execution failed",
                    data: {
                      ...n.data,
                      status: "error",
                      executionState: {
                        ...(n.data.executionState || {}),
                        lastExecuted: new Date().toISOString(),
                        error: data.error || "Workflow execution failed",
                        success: false,
                      },
                    },
                  };
                }
                return n;
              });

              console.log("🔥 After null nodeStatuses processing:");
              updatedNodes.forEach((n) => {
                console.log(
                  `  - Node ${n.id}: status="${n.status}", data.status="${n.data?.status}"`
                );
              });

              return updatedNodes;
            });
            updateWorkflowStatus("failed");
            console.log("🔥 WorkflowFailed with null nodeStatuses - processing complete");
            return; // Exit early for null nodeStatuses case
          }

          // Process nodeStatuses if available
          console.log("🔥 Processing nodeStatuses:", data.nodeStatuses);

          setNodes((currentNodes) => {
            console.log("🔥 Current nodes before update:");
            currentNodes.forEach((n) => {
              console.log(
                `  - Node ${n.id}: status="${n.status}", type="${n.type}", data.label="${n.data?.label}"`
              );
            });

            const updatedNodes = currentNodes.map((n) => {
              // First check if this node has a status in the backend response
              const nodeStatus = data.nodeStatuses?.find((ns) => ns.nodeId === n.id);
              console.log(`🔥 Node ${n.id}: looking for status in backend response...`);
              console.log(`🔥 Node ${n.id}: found nodeStatus:`, nodeStatus);

              if (nodeStatus) {
                // Use the backend status
                const status = nodeStatus.status === "failed" ? "error" : nodeStatus.status;

                console.log(
                  `🔥 Node ${n.id}: mapping backend status "${nodeStatus.status}" to frontend status "${status}"`
                );

                return {
                  ...n,
                  status: status,
                  statusMessage:
                    nodeStatus.error ||
                    (nodeStatus.status === "failed" ? "Node execution failed" : ""),
                  data: {
                    ...n.data,
                    status: status, // Also set status in data object for node components
                    executionState: {
                      ...(n.data.executionState || {}),
                      lastExecuted: nodeStatus.executionTime || new Date().toISOString(),
                      error: nodeStatus.error || null,
                      success: nodeStatus.status === "completed",
                      preview: nodeStatus.sampleData || null,
                      nodeExecutionOutput: nodeStatus.nodeExecutionOutput || null,
                      recordCount: nodeStatus.recordCount || null,
                    },
                  },
                };
              } else {
                // For ANY node not explicitly listed in nodeStatuses, clear running/pending states

                if (n.status === "running" || n.status === "pending" || n.status === "queued") {
                  return {
                    ...n,
                    status: "error",
                    statusMessage: "Workflow failed before this node could execute",
                    data: {
                      ...n.data,
                      status: "error", // Also set status in data object for node components
                      executionState: {
                        ...(n.data.executionState || {}),
                        lastExecuted: new Date().toISOString(),
                        error: "Workflow execution failed",
                        success: false,
                      },
                    },
                  };
                }
                return n;
              }
            });

            updatedNodes.forEach((n) => {
              console.log(
                `  - Node ${n.id}: status="${n.status}", data.status="${n.data?.status}", statusMessage="${n.statusMessage}"`
              );
            });

            return updatedNodes;
          });

          updateWorkflowStatus("failed");

          // Force a state update to ensure React re-renders
          setTimeout(() => {
            setNodes((currentNodes) => {
              currentNodes.forEach((n) => {
                console.log(
                  `  - Node ${n.id}: status="${n.status}", data.status="${n.data?.status}"`
                );
              });
              return [...currentNodes]; // Create new array reference to force re-render
            });
          }, 100);
        }
      },
      onError: (error) => {
        // Handle SignalR connection errors during workflow execution
        const hasActiveExecution = currentExecutionOrderRef.current.length > 0;

        // Use functional update to check current nodes state
        if (hasActiveExecution) {
          setNodes((currentNodes) => {
            const hasRunningNodes = currentNodes.some(
              (n) => n.status === "running" || n.status === "pending"
            );

            if (hasRunningNodes) {
              // Clear timeout since connection is lost and we're failing the workflow
              if (currentTimeoutRef.current) {
                clearTimeout(currentTimeoutRef.current);
                currentTimeoutRef.current = null;
              }

              // Connection lost during active workflow execution - mark as failed
              const updatedNodes = currentNodes.map((n) => {
                if (
                  currentExecutionOrderRef.current.includes(n.id) &&
                  (n.status === "running" || n.status === "pending")
                ) {
                  return {
                    ...n,
                    status: "error",
                    statusMessage: "Connection lost during execution",
                    data: {
                      ...n.data,
                      executionState: {
                        ...(n.data.executionState || {}),
                        lastExecuted: new Date().toISOString(),
                        error: "SignalR connection lost during execution",
                        success: false,
                        connectionLost: true,
                      },
                    },
                  };
                }
                return n;
              });

              // Update workflow status to failed
              updateWorkflowStatus("failed");

              // Clear execution order
              currentExecutionOrderRef.current = [];

              return updatedNodes;
            }
            return currentNodes;
          });
        }
      },
      onConnectionLost: () => {
        // Handle connection loss during workflow execution
        const hasActiveExecution = currentExecutionOrderRef.current.length > 0;

        // Use functional update to check current nodes state
        if (hasActiveExecution) {
          setNodes((currentNodes) => {
            const hasRunningNodes = currentNodes.some(
              (n) => n.status === "running" || n.status === "pending"
            );

            if (hasRunningNodes) {
              // Clear timeout since connection is lost and we're failing the workflow
              if (currentTimeoutRef.current) {
                clearTimeout(currentTimeoutRef.current);
                currentTimeoutRef.current = null;
              }

              const updatedNodes = currentNodes.map((n) => {
                if (
                  currentExecutionOrderRef.current.includes(n.id) &&
                  (n.status === "running" || n.status === "pending")
                ) {
                  return {
                    ...n,
                    status: "error",
                    statusMessage: "Connection lost - execution interrupted",
                    data: {
                      ...n.data,
                      executionState: {
                        ...(n.data.executionState || {}),
                        lastExecuted: new Date().toISOString(),
                        error: "SignalR connection lost during workflow execution",
                        success: false,
                        connectionLost: true,
                      },
                    },
                  };
                }
                return n;
              });

              updateWorkflowStatus("failed");
              currentExecutionOrderRef.current = [];

              return updatedNodes;
            }
            return currentNodes;
          });
        }
      },
    };

    let signalRPromise = null;
    let connectionMonitorInterval = null;

    try {
      signalRPromise = startWorkflowSignalR(eventHandlers, workflow.id);
      if (signalRPromise) {
        signalRPromise
          .then((connection) => {
            if (connection) {
              // Set up connection monitoring during workflow execution
              connectionMonitorInterval = setInterval(() => {
                const hasActiveExecution = currentExecutionOrderRef.current.length > 0;
                const hasRunningNodes = nodes.some(
                  (n) => n.status === "running" || n.status === "pending"
                );

                // Only monitor during active execution
                if (hasActiveExecution && hasRunningNodes) {
                  // Check connection state
                  if (connection.state === "Disconnected" || connection.state === "Disconnecting") {
                    // Connection lost during execution
                    if (eventHandlers.onConnectionLost) {
                      eventHandlers.onConnectionLost();
                    }
                    clearInterval(connectionMonitorInterval);
                  }
                }
              }, 5000); // Check every 5 seconds during execution

              // Handle connection close events
              connection.onclose((error) => {
                const hasActiveExecution = currentExecutionOrderRef.current.length > 0;
                const hasRunningNodes = nodes.some(
                  (n) => n.status === "running" || n.status === "pending"
                );

                if (hasActiveExecution && hasRunningNodes) {
                  if (eventHandlers.onConnectionLost) {
                    eventHandlers.onConnectionLost();
                  }
                }

                if (connectionMonitorInterval) {
                  clearInterval(connectionMonitorInterval);
                }
              });

              // Handle reconnection
              connection.onreconnected(() => {
                // Connection restored - but workflow execution state may be lost
                const hasActiveExecution = currentExecutionOrderRef.current.length > 0;
                const hasRunningNodes = nodes.some(
                  (n) => n.status === "running" || n.status === "pending"
                );

                if (hasActiveExecution && hasRunningNodes) {
                  // After reconnection during execution, mark nodes as failed since we lost state
                  setNodes((nds) =>
                    nds.map((n) => {
                      if (
                        currentExecutionOrderRef.current.includes(n.id) &&
                        (n.status === "running" || n.status === "pending")
                      ) {
                        return {
                          ...n,
                          status: "error",
                          statusMessage: "Connection restored but execution state lost",
                          data: {
                            ...n.data,
                            executionState: {
                              ...(n.data.executionState || {}),
                              lastExecuted: new Date().toISOString(),
                              error: "SignalR reconnected but workflow execution state was lost",
                              success: false,
                              connectionRestored: true,
                            },
                          },
                        };
                      }
                      return n;
                    })
                  );

                  updateWorkflowStatus("failed");
                  currentExecutionOrderRef.current = [];
                }
              });
            } else {
              // Failed to establish connection
              if (eventHandlers.onError) {
                eventHandlers.onError(new Error("Failed to establish SignalR connection"));
              }
            }
          })
          .catch((error) => {
            // Connection establishment failed
            if (eventHandlers.onError) {
              eventHandlers.onError(error);
            }
          });
      }
    } catch (error) {
      if (eventHandlers.onError) {
        eventHandlers.onError(error);
      }
    }

    return () => {
      if (connectionMonitorInterval) {
        clearInterval(connectionMonitorInterval);
      }
      stopWorkflowSignalR();
    };
  }, [workflow?.id]); // Only depend on workflow.id to prevent frequent reconnections

  const handlePlusClick = () => {
    setMode("trigger");
    lastConnectedNode.current = null;
    sidebarRef.current?.open();
  };
  const [highlightedNodeIds, setHighlightedNodeIds] = useState(new Set());
  const [highlightedEdgeIds, setHighlightedEdgeIds] = useState(new Set());

  // Define openSettingsModal early to avoid hoisting issues
  const openSettingsModal = useCallback(
    (nodeId) => {
      try {
        // Use nodes from state instead of enrichedNodes to avoid circular dependency
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) {
          return;
        }

        // Special handling for Excel export node
        const isExcelExportNode = node.data?.type === "data.exporttoexcel";
        if (isExcelExportNode) {
          // Ensure default settings are present
          const defaultSettings = {
            fieldsToInclude: [],
            exportFormat: "excel",
            downloadAsFile: true,
            filenameTemplate: "export-{datetime}",
            includeHeaders: true,
            autoFormat: true,
            applyStyles: true,
          };

          // Create a simpler node object to avoid serialization issues
          const excelNode = {
            id: nodeId,
            type: node.type,
            data: {
              label: node.data?.label || "Excel Export",
              type: "data.exporttoexcel",
              model: node.data?.model || {},
              output: {
                ...node.data?.output,
                settings: {
                  ...defaultSettings,
                  ...node.data?.output?.settings, // Preserve any existing settings
                },
              },
              setData: node.data?.setData,
            },
          };

          // Also update the actual node with defaults if they don't exist
          if (!node.data?.output?.settings) {
            node.data?.setData?.((oldData) => ({
              ...oldData,
              output: {
                ...oldData?.output,
                settings: defaultSettings,
              },
            }));
          }

          setSettingsNode(excelNode);
          setSettingsModalOpen(true);
          return;
        }

        // For other node types, use the normal approach
        setSettingsNode(node);
        setSettingsModalOpen(true);
      } catch (error) {
        // Show a simple error message to the user
        alert("Could not open settings. Please try again.");
      }
    },
    [nodes] // Depend on nodes from state instead
  );

  // Validate if a node can be executed (has required settings, inputs, etc.)
  const validateNodeForExecution = useCallback(
    (node) => {
      if (!node || !node.data) return { isValid: false, errors: ["Invalid node"] };

      try {
        // Debug logging for node validation
        console.log(`🔍 Validating node ${node.id} (${node.type}):`, {
          nodeData: node.data,
          isTrigger: node.type?.startsWith("trigger."),
          requiresSettings: node.data?.requiresSettings,
          outputSettings: node.data?.output?.settings,
        });

        const nodeData = node.data;
        const requiredInputs = nodeData?.model?.inputs || [];

        // Skip input validation for trigger nodes
        const isTriggerNode = node.type?.startsWith("trigger.");

        // Check if node has required inputs connected
        const inputEdges = edges.filter((edge) => edge.target === node.id);
        const hasRequiredInputs =
          isTriggerNode || requiredInputs.length === 0 || inputEdges.length > 0;

        // Check if node has required settings configured
        const requiresSettings = nodeData?.requiresSettings === true;
        const settings = nodeData?.model?.settings || {};
        const requiredSettings = Object.values(settings).filter((s) => s.required);

        // Check node-specific output settings if they exist
        const outputSettings = nodeData?.output?.settings || {};

        // Check if required settings are configured
        const settingsConfigured =
          requiredSettings.length === 0 ||
          requiredSettings.every((s) => s.selectedValue !== undefined && s.selectedValue !== "");

        // If RequiresSettings is true, check if the node has any output settings configured
        const hasAnyOutputSettings = outputSettings && Object.keys(outputSettings).length > 0;
        const hasFieldsToInclude = outputSettings?.fieldsToInclude?.length > 0;
        const hasSelectedModel = !!outputSettings?.selectedModel;
        const hasSelectedSchema = !!outputSettings?.selectedSchema;

        // Check if there are any configured values in the output object directly
        const hasOutputValues =
          nodeData?.output &&
          Object.keys(nodeData.output).some((key) => {
            const value = nodeData.output[key];
            return (
              value !== undefined &&
              value !== null &&
              value !== "" &&
              !(Array.isArray(value) && value.length === 0) &&
              !(typeof value === "object" && Object.keys(value).length === 0)
            );
          });

        // Check model inline settings
        const hasInlineSettings =
          nodeData?.model?.inline &&
          nodeData.model.inline.length > 0 &&
          nodeData.model.inline.some(
            (setting) =>
              setting.selectedValue !== undefined &&
              setting.selectedValue !== null &&
              setting.selectedValue !== ""
          );

        // Special case for model lookup node - don't require output settings
        const isModelLookup =
          node.type?.includes("model.lookup") ||
          nodeData?.name?.includes("modelLookup") ||
          node.type?.includes("modelLookup") ||
          nodeData?.name?.includes("model.lookup");

        // Enhanced output settings validation
        const outputSettingsConfigured =
          isModelLookup ||
          !requiresSettings ||
          hasAnyOutputSettings ||
          hasFieldsToInclude ||
          hasSelectedModel ||
          hasSelectedSchema ||
          hasOutputValues ||
          hasInlineSettings;

        const hasRequiredSettings =
          settingsConfigured && (!requiresSettings || outputSettingsConfigured);

        let hasOutputConfiguration = true;

        // For nodes with fieldsToInclude, check if at least one field is selected
        if (outputSettings && outputSettings.hasOwnProperty("fieldsToInclude")) {
          hasOutputConfiguration = (outputSettings.fieldsToInclude || []).length > 0;
        }

        // For nodes with selectedModel, check if model is selected
        if (outputSettings && outputSettings.hasOwnProperty("selectedModel")) {
          hasOutputConfiguration = !!outputSettings.selectedModel;
        }
        // For model lookup nodes, we'll always consider them valid if they have inputs connected
        // For trigger nodes, we'll consider them valid if they have basic configuration
        const isValid = isTriggerNode
          ? true // Trigger nodes are always valid for execution
          : isModelLookup
            ? hasRequiredInputs
            : hasRequiredInputs && hasRequiredSettings && hasOutputConfiguration;

        // Detailed validation info for debugging
        console.log(`✅ Node ${node.id} validation result:`, {
          isValid,
          isTriggerNode,
          isModelLookup,
          hasRequiredInputs,
          hasRequiredSettings,
          hasOutputConfiguration,
          requiresSettings,
          outputSettings,
        });

        // Detailed error messages to help debugging
        const errors = [];
        if (!hasRequiredInputs) {
          errors.push(`Missing required input connections (${requiredInputs.length} required)`);
        }
        if (!hasRequiredSettings) {
          // Skip validation errors for model lookup nodes
          const isModelLookup =
            node.type?.includes("model.lookup") ||
            node.type?.includes("modelLookup") ||
            nodeData?.name?.includes("model.lookup") ||
            nodeData?.name?.includes("modelLookup");
          if (isModelLookup) {
            // Model lookup nodes are always valid for execution as long as they have required inputs
            // Don't add any errors for model lookup nodes
          } else if (nodeData?.requiresSettings === true) {
            // If the node requires settings (from API model), explain this clearly

            if (
              !hasAnyOutputSettings &&
              !hasFieldsToInclude &&
              !hasSelectedModel &&
              !hasSelectedSchema &&
              !hasOutputValues &&
              !hasInlineSettings
            ) {
              errors.push(`This node type requires settings to be configured in the settings tab`);
            } else if (
              outputSettings?.hasOwnProperty("fieldsToInclude") &&
              outputSettings.fieldsToInclude?.length === 0
            ) {
              errors.push(`This node requires at least one field to be selected`);
            } else if (
              outputSettings?.hasOwnProperty("selectedModel") &&
              !outputSettings.selectedModel
            ) {
              errors.push(`This node requires a model to be selected`);
            } else {
              // Node has some settings but validation still failed - be more specific
              errors.push(`This node requires all settings to be properly configured`);
            }
          } else if (requiredSettings.length > 0) {
            // For individual required settings
            const missingSettings = requiredSettings
              .filter((s) => !s.selectedValue)
              .map((s) => s.name || s.id)
              .join(", ");
            errors.push(`Missing required settings: ${missingSettings}`);
          }
        }

        if (!hasOutputConfiguration) {
          if (
            outputSettings?.hasOwnProperty("fieldsToInclude") &&
            (!outputSettings.fieldsToInclude || outputSettings.fieldsToInclude.length === 0)
          ) {
            errors.push("No fields selected to include in output");
          }
          if (outputSettings?.hasOwnProperty("selectedModel") && !outputSettings.selectedModel) {
            errors.push("No model selected");
          }
        }

        return {
          isValid,
          errors: errors.length > 0 ? errors : ["Node configuration is incomplete"],
        };
      } catch (error) {
        return {
          isValid: false,
          errors: [`Validation error: ${error.message || "Unknown error"}`],
        };
      }
    },
    [edges]
  );

  // Build execution order using proper topological sort that handles multiple input paths
  const buildExecutionOrder = useCallback(
    (startNodeId) => {
      // First, find all nodes reachable from the start node
      const reachableNodes = new Set();
      const visited = new Set();

      const findReachableNodes = (nodeId) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        reachableNodes.add(nodeId);

        // Find all nodes connected downstream from this node
        const connectedEdges = edges.filter((edge) => edge.source === nodeId);
        connectedEdges.forEach((edge) => {
          findReachableNodes(edge.target);
        });
      };

      findReachableNodes(startNodeId);

      // Now perform proper topological sort on reachable nodes only
      const inDegree = new Map();
      const adjList = new Map();

      // Initialize for reachable nodes only
      reachableNodes.forEach((nodeId) => {
        inDegree.set(nodeId, 0);
        adjList.set(nodeId, []);
      });

      // Build adjacency list and calculate in-degrees for reachable nodes only
      edges.forEach((edge) => {
        if (reachableNodes.has(edge.source) && reachableNodes.has(edge.target)) {
          adjList.get(edge.source).push(edge.target);
          inDegree.set(edge.target, inDegree.get(edge.target) + 1);
        }
      });

      // Kahn's algorithm for topological sorting
      const queue = [];
      const executionOrder = [];

      // Start with the trigger node (should have in-degree 0)
      if (inDegree.get(startNodeId) === 0) {
        queue.push(startNodeId);
      }

      // Add any other nodes with in-degree 0 (shouldn't happen in a proper workflow)
      reachableNodes.forEach((nodeId) => {
        if (nodeId !== startNodeId && inDegree.get(nodeId) === 0) {
          queue.push(nodeId);
        }
      });

      while (queue.length > 0) {
        const currentNode = queue.shift();
        executionOrder.push(currentNode);

        // Process all neighbors
        const neighbors = adjList.get(currentNode) || [];
        neighbors.forEach((neighbor) => {
          inDegree.set(neighbor, inDegree.get(neighbor) - 1);

          // If all dependencies are satisfied, add to queue
          if (inDegree.get(neighbor) === 0) {
            queue.push(neighbor);
          }
        });
      }

      // Check for cycles (if executionOrder length !== reachableNodes size, there's a cycle)
      if (executionOrder.length !== reachableNodes.size) {
        console.error("Cycle detected in workflow! Execution order may be incomplete.");
        console.error("Reachable nodes:", Array.from(reachableNodes));
        console.error("Execution order:", executionOrder);

        // Return the partial order we have, but this indicates a problem
        return executionOrder;
      }

      console.log("📋 Execution order:", executionOrder);
      console.log("🔗 Reachable nodes:", Array.from(reachableNodes));

      return executionOrder;
    },
    [edges]
  );

  // Helper function to create NodeExecution objects for the workflow API
  const createNodeExecutions = useCallback(
    (nodeIds = null) => {
      const targetNodes = nodeIds ? nodes.filter((n) => nodeIds.includes(n.id)) : nodes;

      return targetNodes.map((node) => {
        // Get ONLY directly connected input nodes via edges (not upstream nodes)
        const inputEdges = edges.filter((edge) => edge.target === node.id);
        const inputNodes = nodes.filter((n) => inputEdges.some((edge) => edge.source === n.id));

        // Debug logging to identify input issues
        console.log(`📊 Node ${node.id} (${node.data?.label || "Unknown"}):`);
        console.log(
          `   • Input edges:`,
          inputEdges.map((e) => `${e.source} -> ${e.target}`)
        );
        console.log(
          `   • Direct input nodes:`,
          inputNodes.map((n) => `${n.id} (${n.data?.label || "Unknown"})`)
        );

        // Create input data from ONLY directly connected nodes
        const inputData = inputNodes.reduce((acc, inputNode) => {
          const connectingEdges = inputEdges.filter((edge) => edge.source === inputNode.id);

          // Verify this node is actually directly connected
          if (connectingEdges.length === 0) {
            console.warn(
              `⚠️ Node ${inputNode.id} found in inputNodes but has no connecting edges to ${node.id}`
            );
            return acc; // Skip this node if no direct connection
          }

          // If multiple edges from same source, include edge handle info
          if (connectingEdges.length === 1) {
            acc[inputNode.id] = inputNode.data?.output || {};
            console.log(
              `   • Added single input: ${inputNode.id} (${inputNode.data?.label || "Unknown"})`
            );
          } else {
            // Multiple connections from same source - include handle information
            connectingEdges.forEach((edge, index) => {
              const key =
                connectingEdges.length > 1
                  ? `${inputNode.id}_${edge.sourceHandle || index}`
                  : inputNode.id;
              acc[key] = inputNode.data?.output || {};
              console.log(`   • Added multi input: ${key} (${inputNode.data?.label || "Unknown"})`);
            });
          }
          return acc;
        }, {});

        // Enhanced logging for multiple input scenarios
        if (Object.keys(inputData).length > 0) {
          console.log(
            `🔀 Node ${node.id} (${node.data?.label}) final input data keys:`,
            Object.keys(inputData)
          );
        } else {
          console.log(`📭 Node ${node.id} (${node.data?.label}) has no input data`);
        }

        // Create NodeExecution object matching backend C# model
        const nodeExecution = {
          Id: node.id, // Backend expects capitalized 'Id'
          SelectedNode: {
            // Backend Node model properties
            Id: node.id,
            Name: node.data?.name || node.data?.label || `Node ${node.id}`,
            Type: node.type || node.data?.type,
            Category: node.data?.category || "default",
            Label: node.data?.label || node.data?.name || `Node ${node.id}`,
            Description: node.data?.description || "",
            Icon: node.data?.icon || "",
            Model: node.data?.model || {},
            Execution: node.data?.execution || "DefaultExecution",
            Output: JSON.stringify(node.data?.output || {}), // Backend expects Output as JSON string
            Input: JSON.stringify(inputData), // Backend expects Input as JSON string
            RequiresSettings: node.data?.requiresSettings || false,
            Position: node.position,
            // Additional properties that might be expected by backend
            Settings: node.data?.model?.settings || {},
            Version: node.data?.version || "1.0",
            Status: node.data?.status || "idle",
          },
        };

        return nodeExecution;
      });
    },
    [nodes, edges]
  );

  const executNode = useCallback(
    async (nodeId) => {},
    [nodes, edges, setNodes, validateNodeForExecution, createNodeExecutions]
  );

  const executeWorkflow = useCallback(
    async (startNodeId) => {
      // Ensure we have a workflow
      let currentWorkflow = workflow;
      if (!currentWorkflow) {
        // Try to create a workflow if we have nodes
        if (nodes.length > 0) {
          try {
            currentWorkflow = createWorkflow(nodes, edges);

            if (!currentWorkflow) {
              return;
            }

            // Continue with execution using the newly created workflow
          } catch (error) {
            return;
          }
        } else {
          return;
        }
      }

      // Validate the entire workflow

      // Build execution order to validate all connected nodes
      const executionOrder = buildExecutionOrder(startNodeId);

      // Store the execution order for use in completion handling
      currentExecutionOrderRef.current = executionOrder;

      // Validate all nodes in the workflow
      for (const nodeId of executionOrder) {
        const node = nodes.find((n) => n.id === nodeId);
        const validation = validateNodeForExecution(node);

        if (!validation.isValid) {
          // Set the failed node to error status
          setNodes((nds) =>
            nds.map((n) => {
              if (n.id === nodeId) {
                return {
                  ...n,
                  status: "error",
                  statusMessage: validation.errors.join(". "),
                };
              }
              return n;
            })
          );

          // Update workflow status to failed
          updateWorkflowStatus("failed");
          return;
        }
      }

      try {
        // Reset ALL nodes and set execution order nodes to pending in a single operation
        setNodes((nds) =>
          nds.map((n) => {
            // First reset ALL nodes to clean state
            const resetNode = {
              ...n,
              status: "idle",
              statusMessage: "Ready",
              data: {
                ...n.data,
                // Reset execution state data to initial state (keep execution class name)
                executionState: {
                  status: "idle",
                  statusMessage: "Ready",
                },
              },
            };

            // Then set nodes in execution order to pending
            if (executionOrder.includes(n.id)) {
              return {
                ...resetNode,
                status: "pending",
                statusMessage: "Queued for execution",
              };
            }

            return resetNode;
          })
        );

        // Update workflow status to executing
        updateWorkflowStatus("executing");

        // Set up timeout mechanism for workflow execution
        const WORKFLOW_TIMEOUT_MS = 300000; // 5 minutes timeout
        const timeoutId = setTimeout(() => {
          console.warn(`Workflow execution timed out after ${WORKFLOW_TIMEOUT_MS / 1000} seconds`);

          // Mark all running/pending nodes as failed due to timeout
          setNodes((nds) =>
            nds.map((n) => {
              if (
                executionOrder.includes(n.id) &&
                (n.status === "pending" || n.status === "running")
              ) {
                return {
                  ...n,
                  status: "error",
                  statusMessage: "Execution timed out - backend did not respond",
                };
              }
              return n;
            })
          );

          updateWorkflowStatus("failed");
        }, WORKFLOW_TIMEOUT_MS);

        // Store timeout ID for potential cleanup
        currentTimeoutRef.current = timeoutId;

        // Get the workflow payload from the workflow manager
        // Use currentWorkflow to ensure we're using the right workflow object
        const workflowPayload = {
          Id: currentWorkflow.id,
          Name: currentWorkflow.name,
          NodeExecutions: createNodeExecutions(executionOrder),
        };

        // Send the entire workflow to the API endpoint
        // The backend will handle execution and send updates via SignalR

        const response = await callAPI(
          "ai/execution/executeWorkflow", // Single API endpoint for workflow execution
          "POST",
          workflowPayload,
          false
        );

        // Handle the new API response behavior:
        // - OK (200): Workflow is valid and will execute (progress via SignalR)
        // - BadRequest (400): Workflow is not valid
        if (!response.ok) {
          let errorMessage = "Workflow validation failed";

          // Handle BadRequest specifically for workflow validation errors
          if (response.status === 400) {
            try {
              const errorData = await response.json();
              errorMessage =
                errorData.message ||
                errorData.error ||
                "Workflow validation failed - please check your workflow configuration";
            } catch (parseError) {
              errorMessage = "Workflow validation failed - invalid workflow configuration";
            }
          } else {
            // Handle other HTTP errors (500, 401, etc.)
            try {
              const errorData = await response.json();
              errorMessage =
                errorData.message || errorData.error || `Server error: ${response.status}`;
            } catch (parseError) {
              errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            }
          }

          // Set all nodes to error state
          setNodes((nds) =>
            nds.map((n) => {
              if (executionOrder.includes(n.id)) {
                return {
                  ...n,
                  status: "error",
                  statusMessage: errorMessage,
                };
              }
              return n;
            })
          );

          updateWorkflowStatus("failed");

          // Clear timeout since workflow failed
          if (currentTimeoutRef.current) {
            clearTimeout(currentTimeoutRef.current);
            currentTimeoutRef.current = null;
          }

          return;
        }

        // API returned OK (200) - workflow is valid and execution has started
        // Progress updates will come via SignalR and be handled by the event handlers above
      } catch (error) {
        // Set all nodes to error state
        setNodes((nds) =>
          nds.map((n) => {
            if (executionOrder.includes(n.id)) {
              return {
                ...n,
                status: "error",
                statusMessage: `Workflow error: ${error.message || "Unknown error"}`,
              };
            }
            return n;
          })
        );

        updateWorkflowStatus("failed");

        // Clear timeout since workflow failed
        if (currentTimeoutRef.current) {
          clearTimeout(currentTimeoutRef.current);
          currentTimeoutRef.current = null;
        }
      }
    },
    [
      workflow,
      nodes,
      edges,
      buildExecutionOrder,
      validateNodeForExecution,
      setNodes,
      updateWorkflowStatus,
      getWorkflowPayload,
      createWorkflow,
    ]
  );

  const onSelectionChange = useCallback(({ nodes, edges }) => {
    if (nodes.length === 1) {
      const selected = nodes[0];
      // Use the previous state value to check if we need to update
      setSelectedNode((prev) => {
        if (prev?.id !== selected.id) {
          highlightPath(selected.id);
          return selected;
        }
        return prev;
      });
    } else {
      // Clear selection
      setSelectedNode((prev) => {
        if (prev !== null) {
          return null;
        }
        return prev;
      });
      // Clear highlights
      setHighlightedNodeIds((prev) => (prev.size > 0 ? new Set() : prev));
      setHighlightedEdgeIds((prev) => (prev.size > 0 ? new Set() : prev));
    }
  }, []); // Remove dependencies to prevent infinite loop

  const highlightPath = (startNodeId) => {
    const connectedNodes = new Set();
    const connectedEdges = new Set();

    const visitUpstream = (nodeId) => {
      connectedNodes.add(nodeId);
      edges.forEach((edge) => {
        if (edge.target === nodeId) {
          connectedEdges.add(edge.id);
          if (!connectedNodes.has(edge.source)) visitUpstream(edge.source);
        }
      });
    };

    const visitDownstream = (nodeId) => {
      connectedNodes.add(nodeId);
      edges.forEach((edge) => {
        if (edge.source === nodeId) {
          connectedEdges.add(edge.id);
          if (!connectedNodes.has(edge.target)) visitDownstream(edge.target);
        }
      });
    };

    visitUpstream(startNodeId);
    visitDownstream(startNodeId);

    // Only update if the sets have actually changed to avoid unnecessary re-renders
    setHighlightedNodeIds((prev) => {
      if (prev.size !== connectedNodes.size) return connectedNodes;
      for (const nodeId of connectedNodes) {
        if (!prev.has(nodeId)) return connectedNodes;
      }
      return prev; // No change, return previous to avoid re-render
    });

    setHighlightedEdgeIds((prev) => {
      if (prev.size !== connectedEdges.size) return connectedEdges;
      for (const edgeId of connectedEdges) {
        if (!prev.has(edgeId)) return connectedEdges;
      }
      return prev; // No change, return previous to avoid re-render
    });
  };
  const handleAddNode = (nodeDef, position = { x: 200, y: 100 + idCounter * 40 }) => {
    const id = `node-${idCounter}`;
    setIdCounter((c) => c + 1);

    const fullType = `${nodeDef.category}.${nodeDef.type}`;
    const isNote = fullType === "section.note";

    const newNode = {
      id,
      type: fullType,
      category: nodeDef.category,
      position,
      status: "idle", // Initialize status to idle
      statusMessage: "Ready", // Initialize with default status message

      style: isNote ? { zIndex: 0, width: 250, height: 180 } : { zIndex: 2 }, // ✅ ensures note z-index is always 0
      //selectable: !isNote, // optional: prevent note selection outline
      data: isNote
        ? {
            ...nodeDef,
            title: nodeDef.label || "Untitled Note",
            body: nodeDef.description || "Double click to edit me.",
          }
        : {
            ...nodeDef,
            label: nodeDef.label || nodeDef.name || fullType,
            description: nodeDef.description || "",
            // Map RequiresSettings from API to node data (handle both cases)
            requiresSettings:
              nodeDef.requiresSettings === true || nodeDef.RequiresSettings === true,
            execution: nodeDef.execution || "DefaultExecution",
            output: {},
            // Ensure model data is preserved with proper defaults based on node type
            model: (() => {
              const model = nodeDef.model || nodeDef.Model || {};
              return {
                inputs:
                  model.inputs ||
                  model.Inputs ||
                  (fullType.startsWith("trigger.") ? [] : ["input"]),
                outputs:
                  model.outputs ||
                  model.Outputs ||
                  (fullType.startsWith("trigger.") ? ["trigger"] : ["output"]),
                settings: model.settings || model.Settings || {},
                inline: model.inline || model.Inline || [],
                inputLimit: model.inputLimit || model.InputLimit || 0,
                inConnections: model.inConnections || model.InConnections || [],
                outConnections: model.outConnections || model.OutConnections || [],
              };
            })(),
          },
    };
    setNodes((nds) => {
      const newNodes = [...nds, newNode];

      return newNodes;
    });

    // Create workflow if this is the first node (outside setNodes to ensure it executes)
    if (nodes.length === 0 && !workflow) {
      createWorkflow([newNode], []);
    }

    // If we need to create a connection, do it after the node is added
    if (lastConnectedNode.current?.nodeId && !isNote) {
      // Use setTimeout to ensure the node is properly added before creating the edge
      setTimeout(() => {
        const sourceId = lastConnectedNode.current.nodeId;
        const sourceNode = nodes.find((n) => n.id === sourceId);

        // Use proper handle IDs based on node model data
        const sourceHandle = sourceNode?.data.model?.outputs?.[0] || "out";
        const targetHandle = newNode.data.model?.inputs?.[0] || "in";

        setEdges((eds) => [
          ...eds,
          {
            id: `e${sourceId}-${id}`,
            source: sourceId,
            sourceHandle: sourceHandle,
            target: id,
            targetHandle: targetHandle,
            type: "custom",
            style: {
              stroke: "#888",
              strokeWidth: 2,
              strokeOpacity: 1,
            },
          },
        ]);

        // Force ReactFlow refresh after auto-connection
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
          requestAnimationFrame(() => {
            window.dispatchEvent(new Event("resize"));
          });
        }, 50);

        lastConnectedNode.current = null;
      }, 100); // Small delay to ensure node is rendered
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const reactFlowBounds = event.currentTarget.getBoundingClientRect();
    const nodeData = event.dataTransfer.getData("application/reactflow");
    if (!nodeData) return;

    const nodeDef = JSON.parse(nodeData);
    const position = {
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top,
    };

    handleAddNode(nodeDef, position);
  };

  const onConnectStart = (event, { nodeId, handleType, handleId }) => {
    lastConnectedNode.current = { nodeId, handleType, handleId };
  };
  const onConnectEnd = (event) => {
    // Check if drop target is not a valid node handle
    const isOnHandle = event.target.classList.contains("react-flow__handle");
    if (!isOnHandle) {
      // User released drag on empty space - open sidebar to add next node
      sidebarRef.current?.open();
    }
  };

  // Use a ref to cache enriched node data to avoid unnecessary processing during drag
  const enrichedNodesCache = useRef({});

  // Clear cache when edges or nodes change to ensure proper re-rendering
  useEffect(() => {
    enrichedNodesCache.current = {};
  }, [edges.length]); // Only depend on length to avoid clearing on every array recreation

  // Also clear cache when nodes change (for quick node additions)
  useEffect(() => {
    enrichedNodesCache.current = {};
  }, [nodes.length]); // Only depend on length to avoid clearing on every node update

  // Process and enrich nodes only if needed (not during drag operations)
  const enrichedNodes = useMemo(() => {
    return nodes.map((node) => {
      // During drag operations, only update position data and use cached values for everything else
      if (isDragging) {
        // If we have this node in cache, just update its position and return it
        if (enrichedNodesCache.current[node.id]) {
          const cachedNode = enrichedNodesCache.current[node.id];
          return {
            ...cachedNode,
            position: node.position,
            positionAbsolute: node.positionAbsolute,
          };
        }
      }

      // If not dragging or node isn't cached, do full node enrichment
      // Gather all targetHandles connected to this node via edges
      const connectedHandles = new Set(
        edges
          .filter((edge) => edge.target === node.id && edge.targetHandle)
          .map((edge) => edge.targetHandle)
      );

      // Create the enriched node with all necessary data and callbacks
      const enrichedNode = {
        ...node,
        data: {
          ...node.data,
          status: node.status, // Pass through the node status
          setData: (updater) => {
            setNodes((nds) =>
              nds.map((n) => {
                if (n.id !== node.id) return n;

                // Create a deep copy of the current node data without JSON serialization
                const currentData = {
                  ...n.data,
                  model: n.data.model ? { ...n.data.model } : {},
                  output: n.data.output ? { ...n.data.output } : {},
                };

                // If output has settings, preserve them
                if (n.data.output?.settings) {
                  currentData.output.settings = { ...n.data.output.settings };
                }

                // If model has settings, preserve them
                if (n.data.model?.settings) {
                  currentData.model.settings = { ...n.data.model.settings };
                }

                // If model has inline, preserve them
                if (n.data.model?.inline) {
                  currentData.model.inline = [...n.data.model.inline];
                }

                const newData = updater(currentData);

                // Check if settings are now configured and update requiresSettings flag
                if (newData.requiresSettings === true) {
                  let hasConfiguredSettings = false;

                  // Check model settings
                  if (newData.model?.settings && Object.keys(newData.model.settings).length > 0) {
                    hasConfiguredSettings = Object.values(newData.model.settings).some(
                      (setting) =>
                        setting.selectedValue !== undefined &&
                        setting.selectedValue !== "" &&
                        setting.selectedValue !== null
                    );
                  }

                  // Check inline settings
                  if (
                    !hasConfiguredSettings &&
                    newData.model?.inline &&
                    newData.model.inline.length > 0
                  ) {
                    hasConfiguredSettings = newData.model.inline.some(
                      (control) =>
                        control.selectedValue !== undefined &&
                        control.selectedValue !== "" &&
                        control.selectedValue !== null
                    );
                  }

                  // Check output settings
                  if (!hasConfiguredSettings && newData.output?.settings) {
                    const outputSettings = newData.output.settings;
                    hasConfiguredSettings = Object.values(outputSettings).some(
                      (setting) => setting !== undefined && setting !== "" && setting !== null
                    );
                  }

                  // Check for selectedModel and selectedSchema in output (used by model nodes)
                  if (!hasConfiguredSettings && newData.output) {
                    hasConfiguredSettings =
                      (newData.output.selectedModel && newData.output.selectedModel !== "") ||
                      (newData.output.selectedSchema && newData.output.selectedSchema !== "");
                  }

                  // Check model data for any configured values
                  if (!hasConfiguredSettings && newData.model?.data) {
                    hasConfiguredSettings = Object.values(newData.model.data).some(
                      (value) => value !== undefined && value !== "" && value !== null
                    );
                  }

                  // If settings are configured, clear the requiresSettings flag
                  if (hasConfiguredSettings) {
                    newData.requiresSettings = false;
                  }
                }

                return { ...n, data: newData };
              })
            );
          },
          selected: selectedNode?.id === node.id,
          highlighted: highlightedNodeIds.has(node.id),
          connectedHandles, // Pass the connected handles set to node data
          onSettingsClick: () => {
            openSettingsModal(node.id);
          },
          onRun: () => {
            executNode(node.id);
          },
          onExecuteWorkflow: node.type.startsWith("trigger.")
            ? () => {
                executeWorkflow(node.id);
              }
            : undefined,
          onAddNext: () => {
            /* your existing code */
          },
        },
      };
      // Store the enriched node in the cache only if not dragging
      if (!isDragging) {
        enrichedNodesCache.current[node.id] = enrichedNode;
      }
      return enrichedNode;
    });
  }, [
    nodes,
    edges,
    isDragging,
    selectedNode?.id,
    highlightedNodeIds,
    highlightedEdgeIds,
    openSettingsModal,
    executNode,
    executeWorkflow,
    nodes.length, // Add this to ensure recalculation when nodes are added quickly
  ]);

  // Memoize the edge delete function to prevent unnecessary re-renders
  const handleDeleteEdge = useCallback(
    (id) => {
      setEdges((eds) => eds.filter((e) => e.id !== id));
    },
    [setEdges]
  );

  // Memoize stable callback functions to prevent unnecessary re-renders
  const handleDragOver = useCallback((e) => e.preventDefault(), []);

  // Memoize style object to prevent re-creation on every render
  const reactFlowStyle = useMemo(
    () => ({
      backgroundColor: isLight ? "#f5f5f5" : "#333",
    }),
    [isLight]
  );

  const enrichedEdges = useMemo(() => {
    // Early return for empty edges to avoid unnecessary logging and processing
    if (edges.length === 0) {
      return [];
    }

    return edges.map((edge) => {
      const isHighlighted = highlightedEdgeIds.has(edge.id);
      const enrichedEdge = {
        ...edge,
        type: "custom",
        style: {
          stroke: isHighlighted ? "#27BBD9" : "#888",
          strokeWidth: isHighlighted ? 3 : 2,
          strokeOpacity: 1,
        },
        animated: false,
        data: {
          ...edge.data,
          onAddBranch: (id) => {
            // Your logic here
          },
          onDeleteEdge: handleDeleteEdge,
        },
      };
      return enrichedEdge;
    });
  }, [edges, highlightedEdgeIds, handleDeleteEdge]);

  const isValidConnection = (connection) => {
    const { source, target } = connection;

    const sourceNode = nodes.find((n) => n.id === source);
    const targetNode = nodes.find((n) => n.id === target);

    if (!sourceNode || !targetNode) {
      return false;
    }

    // Get inputs and outputs, with fallbacks for nodes without model data
    const targetInputs = targetNode.data.model?.inputs || ["input"];
    const sourceOutputs = sourceNode.data.model?.outputs || ["output"];

    // Check input limit if specified
    const connectedEdges = edges.filter((e) => e.target === targetNode.id);
    const connectedNodesCount = connectedEdges.length;
    const inputLimit = targetNode.data.model?.inputLimit || 0;

    if (inputLimit > 0 && connectedNodesCount >= inputLimit) {
      return false;
    }

    // Basic validation - source must have outputs, target must accept inputs
    const canConnect = sourceOutputs.length > 0 && targetInputs.length > 0;

    // Tool category restriction: tool nodes can only connect to special AI agent handles
    const isSourceTool =
      sourceNode.data.model?.category === "tool" ||
      sourceNode.type?.startsWith("tool.") ||
      sourceNode.data.category === "tool";
    const targetHandle = connection.targetHandle;

    if (isSourceTool) {
      // Tool nodes can only connect to AI agent special handles (chatModel, memory, outputParser)
      // They cannot connect to regular "input" handles
      const allowedToolTargets = ["chatModel", "memory", "outputParser"];

      if (!targetHandle || !allowedToolTargets.includes(targetHandle)) {
        console.log(
          `🚫 Tool node "${sourceNode.data.label}" (type: ${sourceNode.type}) cannot connect to handle "${targetHandle}" - tools can only connect to AI agent special handles (chatModel, memory, outputParser)`
        );
        return false;
      }
    }

    return canConnect;
  };
  const onConnect = (params) => {
    const sourceNode = nodes.find((n) => n.id === params.source);
    const targetNode = nodes.find((n) => n.id === params.target);

    // If no specific handles provided, use the first available ones or defaults
    let sourceHandle = params.sourceHandle;
    let targetHandle = params.targetHandle;

    if (!sourceHandle) {
      if (sourceNode?.data.model?.outputs?.length > 0) {
        sourceHandle = sourceNode.data.model.outputs[0];
      } else {
        sourceHandle = "output";
      }
    }

    if (!targetHandle) {
      if (targetNode?.data.model?.inputs?.length > 0) {
        targetHandle = targetNode.data.model.inputs[0];
      } else {
        targetHandle = "input";
      }
    }

    setEdges((eds) => {
      const newEdge = {
        id: `e${params.source}-${params.target}`,
        source: params.source,
        target: params.target,
        sourceHandle: sourceHandle,
        targetHandle: targetHandle,
        type: "custom",
        style: {
          stroke: "#888",
          strokeWidth: 2,
          strokeOpacity: 1,
        },
      };

      // Check if edge already exists
      const existingEdge = eds.find(
        (e) => e.source === params.source && e.target === params.target
      );

      if (existingEdge) {
        return eds;
      }

      const newEdges = [...eds, newEdge];

      // Force cache clear to ensure proper re-rendering
      setTimeout(() => {
        enrichedNodesCache.current = {};
        // Force ReactFlow refresh after edge creation
        window.dispatchEvent(new Event("resize"));
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event("resize"));
        });
      }, 0);

      return newEdges;
    });
  };

  const closeSettingsModal = () => {
    setSettingsModalOpen(false);
    setSettingsNode(null);
  };

  // Force ReactFlow to re-render when nodes or edges change
  useEffect(() => {
    // Multiple strategies to force ReactFlow re-render
    const forceRefresh = () => {
      // Dispatch resize event to force ReactFlow layout recalculation
      window.dispatchEvent(new Event("resize"));

      // Also try requestAnimationFrame for next frame
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    };

    // Immediate refresh
    forceRefresh();

    // Small delay refresh for edge rendering issues
    const timeoutId = setTimeout(forceRefresh, 100);

    return () => clearTimeout(timeoutId);
  }, [nodes.length, edges.length]); // Only depend on length to avoid excessive refreshes

  // Debug: Add window functions for testing SignalR
  useEffect(() => {
    window.debugSignalR = {
      getConnectionState: () => {
        const connection = getWorkflowConnection();
        return {
          exists: !!connection,
          state: connection?.state,
          connectionId: connection?.connectionId,
          registeredEvents: connection ? Object.keys(connection._callbacks || {}) : [],
        };
      },
      getUserInfo: async () => {
        try {
          const user = await getCurrentUser();
          return user;
        } catch (error) {
          return null;
        }
      },
      simulateWorkflowCompleted: (nodeStatuses = []) => {
        // Simulate WorkflowCompleted event for testing
        const mockData = {
          id: workflow?.id,
          status: "completed",
          nodeStatuses: nodeStatuses.length > 0 ? nodeStatuses : undefined,
        };

        // Call the WorkflowCompleted handler logic directly
        const dataWorkflowId = mockData.id || mockData.Id;
        if (dataWorkflowId === workflow?.id) {
          if (mockData.nodeStatuses && Array.isArray(mockData.nodeStatuses)) {
            setNodes((nds) =>
              nds.map((n) => {
                const nodeStatus = mockData.nodeStatuses.find((ns) => ns.nodeId === n.id);
                if (nodeStatus) {
                  const hasError =
                    nodeStatus.status === "failed" ||
                    nodeStatus.status === "error" ||
                    nodeStatus.error;
                  const isSuccessful =
                    !hasError &&
                    (nodeStatus.status === "completed" || nodeStatus.status === "success");
                  const statusMessage = hasError
                    ? nodeStatus.error || nodeStatus.message || "Failed"
                    : nodeStatus.message || "Completed successfully";

                  return {
                    ...n,
                    status: hasError ? "error" : "completed",
                    statusMessage: statusMessage,
                  };
                } else {
                  if (
                    n.status === "running" ||
                    n.status === "pending" ||
                    currentExecutionOrderRef.current.includes(n.id)
                  ) {
                    return {
                      ...n,
                      status: "completed",
                      statusMessage: "Completed successfully",
                    };
                  }
                }
                return n;
              })
            );
          } else {
            setNodes((nds) =>
              nds.map((n) => {
                if (currentExecutionOrderRef.current.includes(n.id)) {
                  return {
                    ...n,
                    status: "completed",
                    statusMessage: "Completed successfully",
                  };
                }
                return n;
              })
            );
          }
        }
      },
    };

    return () => {
      delete window.debugSignalR;
    };
  }, [workflow?.id, nodes, getCurrentUser, setNodes, currentExecutionOrderRef]);

  // Add Excel export debugging functions to help diagnose issues
  useEffect(() => {
    window.debugExcelExport = {
      checkExcelNodes: () => {
        const excelNodes = nodes.filter(
          (node) =>
            node.type === "export.excel" ||
            node.data?.type === "export.excel" ||
            node.data?.type === "data.exporttoexcel"
        );

        excelNodes.forEach((node) => {});

        return excelNodes;
      },

      simulateExcelCompletion: (nodeId, filename = "test-export.xlsx") => {
        const testData = {
          id: workflow?.id,
          nodeId: nodeId,
          status: "completed",
          message: "Excel export completed successfully",
          downloadUrl: "https://example.com/download/test-file.xlsx",
          filename: filename,
          step: 1,
          totalSteps: 1,
        };

        return testData;
      },
    };

    return () => {
      delete window.debugExcelExport;
    };
  }, [nodes, workflow?.id]);

  // Add node status debugging functions to help diagnose error handling
  useEffect(() => {
    window.debugNodeStatus = {
      checkAllNodes: () => {
        return nodes.map((n) => ({
          id: n.id,
          type: n.type,
          status: n.status,
          statusMessage: n.statusMessage,
          label: n.data?.label,
        }));
      },

      checkErrorNodes: () => {
        return nodes
          .filter((n) => n.status === "error")
          .map((n) => ({
            id: n.id,
            type: n.type,
            status: n.status,
            statusMessage: n.statusMessage,
            error: n.data?.executionState?.error,
          }));
      },

      checkCompletedNodes: () => {
        return nodes
          .filter((n) => n.status === "completed")
          .map((n) => ({
            id: n.id,
            type: n.type,
            status: n.status,
            statusMessage: n.statusMessage,
          }));
      },

      getCurrentExecutionOrder: () => {
        return currentExecutionOrderRef.current;
      },

      checkExecutionOrder: () => {
        const executionOrder = currentExecutionOrderRef.current;
        return executionOrder.map((nodeId) => {
          const node = nodes.find((n) => n.id === nodeId);
          return {
            id: nodeId,
            found: !!node,
            status: node?.status,
            statusMessage: node?.statusMessage,
            type: node?.type,
            label: node?.data?.label,
          };
        });
      },

      getLastEvents: () => {
        return {
          lastNodeExecutionCompleted: window.lastNodeExecutionCompleted,
          lastWorkflowCompleted: window.lastWorkflowCompleted,
        };
      },

      trackNodeStatusChanges: () => {
        const changes = [];
        const originalSetNodes = setNodes;

        // Override setNodes temporarily to track changes
        window.trackedSetNodes = (updater) => {
          originalSetNodes((nds) => {
            const oldNodes = nds;
            const newNodes = typeof updater === "function" ? updater(nds) : updater;

            // Track status changes
            newNodes.forEach((newNode, index) => {
              const oldNode = oldNodes[index];
              if (
                oldNode &&
                (oldNode.status !== newNode.status ||
                  oldNode.statusMessage !== newNode.statusMessage)
              ) {
                changes.push({
                  timestamp: new Date().toISOString(),
                  nodeId: newNode.id,
                  oldStatus: oldNode.status,
                  newStatus: newNode.status,
                  oldMessage: oldNode.statusMessage,
                  newMessage: newNode.statusMessage,
                });
              }
            });

            return newNodes;
          });
        };

        return {
          changes,
          stop: () => {
            delete window.trackedSetNodes;
          },
        };
      },

      simulateError: (nodeId, errorMessage = "Test error from debug") => {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  status: "error",
                  statusMessage: errorMessage,
                  data: {
                    ...n.data,
                    executionState: {
                      ...n.data?.executionState,
                      lastExecuted: new Date().toISOString(),
                      error: errorMessage,
                      success: false,
                    },
                  },
                }
              : n
          )
        );
      },

      resetAllNodes: () => {
        setNodes((nds) =>
          nds.map((n) => ({
            ...n,
            status: "idle",
            statusMessage: "Ready",
            data: {
              ...n.data,
              executionState: {
                status: "idle",
                statusMessage: "Ready",
              },
            },
          }))
        );
      },

      testExecutionReset: (startNodeId = null) => {
        // Find the first trigger node if no startNodeId provided
        const triggerNode = startNodeId
          ? nodes.find((n) => n.id === startNodeId)
          : nodes.find((n) => n.type === "trigger.manual");
        if (!triggerNode) {
          return { error: "No trigger node found" };
        }

        // Build execution order
        const executionOrder = buildExecutionOrder(triggerNode.id);
        currentExecutionOrderRef.current = executionOrder;

        // Reset nodes exactly like executeWorkflow does
        setNodes((nds) =>
          nds.map((n) => {
            // First reset ALL nodes to clean state
            const resetNode = {
              ...n,
              status: "idle",
              statusMessage: "Ready",
              data: {
                ...n.data,
                executionState: {
                  status: "idle",
                  statusMessage: "Ready",
                },
              },
            };

            // Then set nodes in execution order to pending
            if (executionOrder.includes(n.id)) {
              return {
                ...resetNode,
                status: "pending",
                statusMessage: "Queued for execution",
              };
            }

            return resetNode;
          })
        );

        return {
          success: true,
          executionOrder: executionOrder,
          triggerNodeId: triggerNode.id,
        };
      },

      testCompletion: (nodeStatuses = []) => {
        // Test the completion logic
        const executionOrder = currentExecutionOrderRef.current;

        if (nodeStatuses.length > 0) {
          // Test with provided nodeStatuses
          setNodes((nds) =>
            nds.map((n) => {
              const nodeStatus = nodeStatuses.find((ns) => ns.nodeId === n.id);
              if (nodeStatus) {
                const hasError =
                  nodeStatus.status === "failed" ||
                  nodeStatus.status === "error" ||
                  nodeStatus.error;
                const isSuccessful =
                  !hasError &&
                  (nodeStatus.status === "completed" || nodeStatus.status === "success");
                const statusMessage = hasError
                  ? nodeStatus.error || nodeStatus.message || "Failed"
                  : nodeStatus.message || "Completed successfully";

                return {
                  ...n,
                  status: hasError ? "error" : "completed",
                  statusMessage: statusMessage,
                };
              } else {
                if (
                  n.status === "running" ||
                  n.status === "pending" ||
                  executionOrder.includes(n.id)
                ) {
                  return {
                    ...n,
                    status: "completed",
                    statusMessage: "Completed successfully",
                  };
                }
              }
              return n;
            })
          );
        } else {
          // Test without nodeStatuses (should complete all execution order nodes)
          setNodes((nds) =>
            nds.map((n) => {
              if (executionOrder.includes(n.id)) {
                return {
                  ...n,
                  status: "completed",
                  statusMessage: "Completed successfully",
                  data: {
                    ...n.data,
                    executionState: {
                      ...(n.data.executionState || {}),
                      lastExecuted: new Date().toISOString(),
                      success: true,
                    },
                  },
                };
              }
              return n;
            })
          );
        }

        return {
          success: true,
          executionOrder: executionOrder,
          nodeStatusesProvided: nodeStatuses.length > 0,
        };
      },
    };

    return () => {
      delete window.debugNodeStatus;
    };
  }, [nodes, setNodes, currentExecutionOrderRef, buildExecutionOrder]);

  // Add keyboard shortcut handlers
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for specific key combinations
      if (event.shiftKey && event.key.toLowerCase() === "s" && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        handleAddNode(
          {
            category: "section",
            type: "note",
            label: "Note",
            description: "",
          },
          { x: 200, y: 100 + nodes.length * 40 }
        );
      } else if (
        event.shiftKey &&
        event.altKey &&
        event.key.toLowerCase() === "t" &&
        !event.ctrlKey
      ) {
        event.preventDefault();
        controlsRef.current?.handleAutoLayout();
      } else if (
        event.ctrlKey &&
        event.key.toLowerCase() === "a" &&
        !event.shiftKey &&
        !event.altKey
      ) {
        event.preventDefault();
        // Toggle select all / deselect all
        const hasSelectedNodes = nodes.some((node) => node.selected);
        const hasSelectedEdges = edges.some((edge) => edge.selected);
        const shouldDeselect = hasSelectedNodes || hasSelectedEdges;

        setNodes((prevNodes) =>
          prevNodes.map((node) => ({
            ...node,
            selected: !shouldDeselect,
          }))
        );

        setEdges((prevEdges) =>
          prevEdges.map((edge) => ({
            ...edge,
            selected: !shouldDeselect,
          }))
        );
      } else if (event.shiftKey && event.key === "Tab" && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        setMode("trigger");
        lastConnectedNode.current = null;
        sidebarRef.current?.open();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleAddNode, nodes, edges, setNodes, setEdges, controlsRef]);

  return (
    <Box>
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <Box title="Overview">
          <WorkflowsOverview
            onCreateWorkflow={async (workflowData, switchToEditor = false) => {
              try {
                const newWorkflow = await createWorkflow(workflowData);
                // Clear current nodes and edges for new workflow
                setNodes([]);
                setEdges([]);
                setIdCounter(1);
                // Switch to editor tab if requested
                if (switchToEditor) {
                  setActiveTab(1); // Editor tab is index 1
                }
              } catch (error) {
                console.error("Failed to create workflow:", error);
              }
            }}
            onOpenWorkflow={async (workflowData, switchToEditor = false) => {
              try {
                // Load workflow data into the editor
                if (workflowData.nodes) {
                  setNodes(workflowData.nodes);
                }
                if (workflowData.edges) {
                  setEdges(workflowData.edges);
                }
                // Update workflow context
                await updateWorkflow(workflowData);
                // Switch to editor tab if requested
                if (switchToEditor) {
                  setActiveTab(1); // Editor tab is index 1
                }
              } catch (error) {
                console.error("Failed to open workflow:", error);
              }
            }}
            currentWorkflow={workflow}
          />
        </Box>
        <Box title="Editor">
          <ReactFlowProvider>
            <Box
              onContextMenu={(e) => {
                e.preventDefault();
                const bounds = e.currentTarget.getBoundingClientRect();
                setContextMenu({
                  mouseX: e.clientX - bounds.left,
                  mouseY: e.clientY - bounds.top,
                });
              }}
              sx={{ position: "relative", height: "calc(100vh - 60px)", width: "100%" }}
            >
              {" "}
              {nodes.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  {" "}
                  <Tooltip title="Start building report">
                    <IconButton onClick={handlePlusClick} color="primary" sx={{ fontSize: 64 }}>
                      <AddCircleOutlineIcon
                        sx={{ fontSize: 64, color: isLight ? "#1976d2" : "#fff" }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <ReactFlow
                key={`${nodes.length}-${edges.length}`} // Force re-render when nodes/edges change
                isValidConnection={isValidConnection}
                nodes={enrichedNodes}
                edges={enrichedEdges}
                edgeTypes={edgeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onSelectionChange={onSelectionChange}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                onConnect={onConnect}
                onPaneContextMenu={(event) => {
                  event.preventDefault();
                  const bounds = event.currentTarget.getBoundingClientRect();
                  setContextMenu({
                    mouseX: event.clientX - bounds.left,
                    mouseY: event.clientY - bounds.top,
                  });
                }}
                onPaneClick={() => setContextMenu(null)}
                onPaneDoubleClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                nodeTypes={nodeTypes}
                selectionMode="partial"
                doubleClickToZoom={false}
                zoomOnDoubleClick={false}
                style={reactFlowStyle}
              >
                <Background />
                <ReportBuilderControls ref={controlsRef} />
                <ReportBuilderMiniMap />
              </ReactFlow>
              {contextMenu !== null && (
                <>
                  {/* Invisible overlay to capture outside clicks */}
                  <Box
                    sx={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1999,
                    }}
                    onClick={() => setContextMenu(null)}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: contextMenu.mouseY,
                      left: contextMenu.mouseX,
                      backgroundColor: isLight ? "#ffffff" : "#2d2d2d",
                      border: `1px solid ${isLight ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)"}`,
                      borderRadius: "8px",
                      boxShadow: isLight
                        ? "0px 4px 20px rgba(0, 0, 0, 0.15)"
                        : "0px 4px 20px rgba(0, 0, 0, 0.4)",
                      zIndex: 2000,
                      minWidth: 200,
                      py: 1,
                      overflow: "hidden",
                    }}
                    onMouseLeave={() => setContextMenu(null)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        color: isLight ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: isLight
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      onClick={() => {
                        setMode("trigger");
                        lastConnectedNode.current = null;
                        sidebarRef.current?.open();
                        setContextMenu(null);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ fontSize: "16px" }}>➕</Box>
                        <Box>Add node</Box>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: isLight
                            ? "rgba(0, 0, 0, 0.06)"
                            : "rgba(255, 255, 255, 0.1)",
                          color: isLight ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)",
                          fontSize: "11px",
                          fontWeight: 600,
                          px: 0.75,
                          py: 0.25,
                          borderRadius: "4px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Shift+Tab
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        color: isLight ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: isLight
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      onClick={() => {
                        handleAddNode(
                          {
                            category: "section",
                            type: "note",
                            label: "Note",
                            description: "",
                          },
                          { x: contextMenu.mouseX, y: contextMenu.mouseY }
                        );
                        setContextMenu(null);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ fontSize: "16px" }}>🗒️</Box>
                        <Box>Add sticky note</Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Box
                          sx={{
                            backgroundColor: isLight
                              ? "rgba(0, 0, 0, 0.06)"
                              : "rgba(255, 255, 255, 0.1)",
                            color: isLight ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)",
                            fontSize: "11px",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: "4px",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Shift+S
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        height: "1px",
                        backgroundColor: isLight
                          ? "rgba(0, 0, 0, 0.08)"
                          : "rgba(255, 255, 255, 0.08)",
                        mx: 1,
                        my: 0.5,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        color: isLight ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: isLight
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      onClick={() => {
                        // Use the existing auto layout function from controls
                        controlsRef.current?.handleAutoLayout();
                        setContextMenu(null);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ fontSize: "16px" }}>✨</Box>
                        <Box>Tidy up workflow</Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Box
                          sx={{
                            backgroundColor: isLight
                              ? "rgba(0, 0, 0, 0.06)"
                              : "rgba(255, 255, 255, 0.1)",
                            color: isLight ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)",
                            fontSize: "11px",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                            marginLeft: "30px",
                            borderRadius: "4px",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Shift+Alt+T
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        height: "1px",
                        backgroundColor: isLight
                          ? "rgba(0, 0, 0, 0.08)"
                          : "rgba(255, 255, 255, 0.08)",
                        mx: 1,
                        my: 0.5,
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        color: isLight ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: isLight
                            ? "rgba(0, 0, 0, 0.04)"
                            : "rgba(255, 255, 255, 0.08)",
                        },
                      }}
                      onClick={() => {
                        // Toggle select all / deselect all
                        const hasSelectedNodes = nodes.some((node) => node.selected);
                        const hasSelectedEdges = edges.some((edge) => edge.selected);
                        const shouldDeselect = hasSelectedNodes || hasSelectedEdges;

                        setNodes((prevNodes) =>
                          prevNodes.map((node) => ({
                            ...node,
                            selected: !shouldDeselect,
                          }))
                        );

                        setEdges((prevEdges) =>
                          prevEdges.map((edge) => ({
                            ...edge,
                            selected: !shouldDeselect,
                          }))
                        );

                        setContextMenu(null);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{ fontSize: "16px" }}>
                          {(() => {
                            const hasSelectedNodes = nodes.some((node) => node.selected);
                            const hasSelectedEdges = edges.some((edge) => edge.selected);
                            return hasSelectedNodes || hasSelectedEdges ? "☑️" : "⬜";
                          })()}
                        </Box>
                        <Box>
                          {(() => {
                            const hasSelectedNodes = nodes.some((node) => node.selected);
                            const hasSelectedEdges = edges.some((edge) => edge.selected);
                            return hasSelectedNodes || hasSelectedEdges
                              ? "Deselect all"
                              : "Select all";
                          })()}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Box
                          sx={{
                            backgroundColor: isLight
                              ? "rgba(0, 0, 0, 0.06)"
                              : "rgba(255, 255, 255, 0.1)",
                            color: isLight ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)",
                            fontSize: "11px",
                            fontWeight: 600,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: "4px",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Ctrl+A
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            {settingsModalOpen && (
              <SettingsModal
                open={settingsModalOpen}
                onClose={closeSettingsModal}
                node={settingsNode}
              >
                {React.createElement(ModalTypes[settingsNode.type], {
                  open: true,
                  node: settingsNode,
                  onClose: () => setCustomSettingsModalOpen(null),
                })}
              </SettingsModal>
            )}
          </ReactFlowProvider>
        </Box>
        <Box title="Visualizer">
          <WorkflowView
            nodes={nodes}
            edges={edges}
            executionState={nodes.reduce((acc, node) => {
              acc[node.id] = {
                status: node.status || "idle",
                output: node.data?.output,
                duration: node.duration,
                recordsProcessed: node.recordsProcessed,
                memoryUsage: node.memoryUsage,
                startTime: node.startTime,
                error: node.statusMessage,
                // Include file result information for download buttons
                fileResult: node.data?.executionState?.fileResult,
                downloadUrl: node.data?.executionState?.downloadUrl,
                filename: node.data?.executionState?.filename,
              };
              return acc;
            }, {})}
            onExecuteNode={(nodeId) => {
              // You can add node execution logic here if needed
            }}
          />
        </Box>
        <Box title="Executions">
          <ExecutionsView
            nodes={nodes}
            edges={edges}
            executionState={nodes.reduce((acc, node) => {
              // Map real execution data from node.data.executionState
              const execution = node.data?.executionState;
              if (execution) {
                acc[node.id] = {
                  status: node.status || "idle",
                  startTime: execution.lastExecuted,
                  endTime: execution.success ? execution.lastExecuted : null,
                  duration: execution.duration,
                  error:
                    node.status === "error" || node.status === "failed"
                      ? execution.error || node.statusMessage
                      : execution.error,
                  step: execution.step,
                  success: execution.success,
                };
              } else if (node.status && node.status !== "idle") {
                // Include nodes that have status but no execution data yet
                acc[node.id] = {
                  status: node.status,
                  // Only set error message if status indicates failure
                  error:
                    node.status === "error" || node.status === "failed" ? node.statusMessage : null,
                };
              }
              return acc;
            }, {})}
          />
        </Box>
      </Tabs>

      <ReportBuilderViewSideBar ref={sidebarRef} onAddNode={handleAddNode} nodes={nodes} />
    </Box>
  );
};
